import React, { useState, useEffect } from 'react';
import { BaseTool, IToolManifest, IToolComponentProps, IToolContext } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { CreateShapeCommand } from '../../core/commands/CreateShapeCommand';
import { CreateEdgeCommand } from '../../core/commands/CreateEdgeCommand';
import { validateShapeLimitOrPromptUpgrade } from '../../utils/shapeLimitUtils';
import { useDiagramStore } from '../../stores/diagramStore';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import {
  Square, Circle, Triangle, Diamond, Hexagon, Pentagon, Star, Minus,
  ChevronDown, Table, Cloud, Server, Router, Database, HardDrive, Laptop, Monitor, Shield, Network as NetworkIcon, User,
  Search, X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { GenogramShapePreview } from '../../react-app/components/GenogramShapePreview';
import { TableTemplatePicker } from '@/components/tables/TableTemplatePicker';
const OrgShapePreview = React.lazy(() =>
  import('../../domains/orgchart/components/OrgShapePreview').then((module) => ({
    default: module.OrgShapePreview,
  }))
);
import { getShapeManifest, type DiagramType } from '../../lib/diagram/registries';
import {
  listRegisteredShapeCategories,
  getRegisteredCategoryIdsForDiagramType,
  type RegisteredShapeCategory,
} from '../../lib/diagram/shapeCategoryRegistry';
import { resolveRegisteredShapeCategoryManifest } from '../../lib/diagram/shapeCategoryManifestResolver';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n/config';
import { AI_FLOWCHART_SHAPES, AI_FLOWCHART_SHAPE_IDS } from '../../domains/process/shapes/flowchart/FlowchartShapes.defs';
import {
  getTranslatedBpmnShapeLabel,
  getTranslatedFlowchartShapeLabel,
} from '../../domains/process/shapes/flowchart/flowchartShapeI18n';
import { BPMN_SHAPES, BPMN_SHAPE_IDS } from '../../domains/process/shapes/bpmn/BpmnShapes.defs';
import {
  NETWORK_SHAPE_LIBRARY_GROUPS,
  orderNetworkShapeLibraryGroups,
  type NetworkShapePickerItem,
} from './networkShapePickerLibraries';
import {
  DEFAULT_NETWORK_SHAPE_LIBRARY_ID,
  isNetworkShapeLibraryId,
  type NetworkShapeLibraryId,
} from '../../domains/network/shapes/networkShapeLibraryCatalog';
import { createTableFromSchema } from '@/domains/diagram/shapes/table/createTableFromSchema';
import { getTableSchema } from '@/domains/diagram/shapes/table/tableSchemas.registry';
import { ORG_SHAPE_CATALOG } from '@/domains/orgchart/models/orgShapeCatalog';
import { createNetworkElementCommand } from '@/domains/network/commands';
import {
  filterShapePickerItems,
  normalizeShapePickerSearch,
} from '../../lib/diagram/shapePickerSearch';
import {
  resolveShapePickerManifestDimensions,
  resolveShapePickerOrgViewMode,
} from './shapePickerDimensions';

const toTitleCaseFromId = (value: string): string =>
  value
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const createAiFlowchartIcon = (shapeId: string): React.FC<{ className?: string }> => {
  const def = ({
    ...(AI_FLOWCHART_SHAPES as Record<string, { width: number; height: number; strokeWidth?: number; paths: string[] }>),
    ...(BPMN_SHAPES as Record<string, { width: number; height: number; strokeWidth?: number; paths: string[] }>),
  } as Record<string, { width: number; height: number; strokeWidth?: number; paths: string[] }>)[shapeId];
  return ({ className }) => (
    <svg
      viewBox={`0 0 ${def?.width ?? 24} ${def?.height ?? 24}`}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={String(def?.strokeWidth ?? 1.6)}
      strokeLinecap="butt"
      strokeLinejoin="miter"
    >
      {(def?.paths ?? []).map((d, idx) => (
        <path
          key={`ai-flowchart-icon-path-${shapeId}-${idx}`}
          d={d}
          fillRule="evenodd"
          clipRule="evenodd"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
};

// Inline icons for Swimlane shapes per request
const SwimlaneVerticalIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    transform="rotate(-90)"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M15 3v18" />
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M21 9H3" />
    <path d="M21 15H3" />
  </svg>
);

const SwimlaneHorizontalIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    transform="rotate(180)"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M15 3v18" />
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M21 9H3" />
    <path d="M21 15H3" />
  </svg>
);

// Type for shape categories with optional connectors
type ShapeCategory = {
  name: string;
  shapes?: ShapePickerShape[];
  connectors?: Array<{ id: string; label: string; icon: ShapeIconComponent; variant?: string }>;
};

type ShapeIconComponent = React.ComponentType<{ className?: string }>;
type GenogramPreviewShapeType = React.ComponentProps<typeof GenogramShapePreview>['shapeType'];
type OrgPreviewShapeType = React.ComponentProps<typeof OrgShapePreview>['shapeType'];
type ShapePickerNodeLike = { attrs?: Record<string, unknown>; name?: unknown; type?: unknown };
type ShapePickerWindow = Window & { __openShapePicker?: () => void };

type ShapePickerShape = {
  id: string;
  label: string;
  icon?: ShapeIconComponent;
  iconUrl?: string;
  variant?: string;
  preview?: string;
  executeCategory?: string;
  defaultText?: string;
  libraryId?: NetworkShapeLibraryId;
};

function iconForBasicNetworkResource(resourceType: unknown): ShapeIconComponent {
  switch (resourceType) {
    case 'router': return Router;
    case 'switch': return NetworkIcon;
    case 'firewall': return Shield;
    case 'server': return Server;
    case 'laptop': return Laptop;
    case 'workstation': return Monitor;
    case 'database': return Database;
    case 'storage': return HardDrive;
    case 'cloud':
    case 'internet': return Cloud;
    case 'user': return User;
    case 'load_balancer': return NetworkIcon;
    default: return Cloud;
  }
}

function getShapeIconName(shape: ShapePickerShape): string | undefined {
  const icon = shape.icon as (ShapeIconComponent & { name?: string }) | undefined;
  return icon?.name;
}

function toNetworkPickerShape(item: NetworkShapePickerItem): ShapePickerShape {
  return {
    id: item.id,
    label: item.label,
    iconUrl: item.iconUrl,
    icon: item.libraryId === 'base' ? iconForBasicNetworkResource(item.resourceType) : Cloud,
    libraryId: item.libraryId,
    executeCategory: 'network',
  };
}

function readNetworkDefaultShapeLibrary(doc: IToolContext['doc'] | undefined): NetworkShapeLibraryId | null {
  try {
    const settingsMap = doc?.getMap?.('diagramSettings');
    const settingsJson = settingsMap?.get?.('settings');
    if (!settingsJson || typeof settingsJson !== 'string') return DEFAULT_NETWORK_SHAPE_LIBRARY_ID;
    const settings = JSON.parse(settingsJson) as { network?: { defaultShapeLibrary?: unknown } };
    const libraryId = settings.network?.defaultShapeLibrary;
    return isNetworkShapeLibraryId(libraryId) ? libraryId : DEFAULT_NETWORK_SHAPE_LIBRARY_ID;
  } catch {
    return DEFAULT_NETWORK_SHAPE_LIBRARY_ID;
  }
}

const NETWORK_PICKER_SHAPES = NETWORK_SHAPE_LIBRARY_GROUPS.flatMap((group) =>
  group.shapes.map(toNetworkPickerShape),
);

const NETWORK_SHAPE_CATEGORY_PREFIX = 'network-';

function toNetworkShapeCategoryKey(libraryId: NetworkShapeLibraryId): string {
  return `${NETWORK_SHAPE_CATEGORY_PREFIX}${libraryId}`;
}

function getNetworkShapeLibraryTabLabel(libraryId: NetworkShapeLibraryId): string {
  switch (libraryId) {
    case 'aws':
      return 'AWS';
    case 'base':
      return 'Base';
    case 'azure':
      return 'Azure';
    case 'gcp':
      return 'GCP';
    case 'kubernetes':
      return 'K8s';
    case 'generic':
      return 'Generic';
    default:
      return String(libraryId).toUpperCase();
  }
}

const NETWORK_SHAPE_CATEGORIES = Object.fromEntries(
  NETWORK_SHAPE_LIBRARY_GROUPS.map((group) => [
    toNetworkShapeCategoryKey(group.id),
    {
      name: getNetworkShapeLibraryTabLabel(group.id),
      shapes: group.shapes.map(toNetworkPickerShape),
    } satisfies ShapeCategory,
  ]),
) as Record<string, ShapeCategory>;

function getOrderedNetworkShapeCategoryKeys(preferredLibraryId: NetworkShapeLibraryId | null): string[] {
  return orderNetworkShapeLibraryGroups(NETWORK_SHAPE_LIBRARY_GROUPS, preferredLibraryId)
    .map((group) => toNetworkShapeCategoryKey(group.id));
}

// Shape categories based on diagram type
const shapeCategories: Record<string, ShapeCategory> = {
  genogram: {
    name: 'Genogram',
    shapes: [
      { id: 'male', label: 'Male', preview: 'male' },
      { id: 'female', label: 'Female', preview: 'female' },
      { id: 'transgender-male', label: 'Transgender Male', preview: 'transgender-male' },
      { id: 'transgender-female', label: 'Transgender Female', preview: 'transgender-female' },
      { id: 'index-person', label: 'Index Person', preview: 'index-person' },
      { id: 'pregnant', label: 'Pregnant', preview: 'pregnant' },
      { id: 'gay', label: 'Gay', preview: 'gay' },
      { id: 'lesbian', label: 'Lesbian', preview: 'lesbian' },
      { id: 'bisexual-male', label: 'Bisexual Male', preview: 'bisexual-male' },
      { id: 'bisexual-female', label: 'Bisexual Female', preview: 'bisexual-female' },
      { id: 'stillbirth-male', label: 'Stillbirth Male', preview: 'stillbirth-male' },
      { id: 'stillbirth-female', label: 'Stillbirth Female', preview: 'stillbirth-female' },
      { id: 'deceased-male', label: 'Deceased Male', preview: 'deceased-male' },
      { id: 'deceased-female', label: 'Deceased Female', preview: 'deceased-female' },
      { id: 'deceased-unknown', label: 'Deceased Unknown', preview: 'deceased-unknown' },
      { id: 'institution', label: 'Institution', preview: 'institution' },
      { id: 'pet', label: 'Pet', preview: 'pet' },
      { id: 'culture-male', label: 'More Than 2 Culture Male', preview: 'culture-male' },
      { id: 'culture-female', label: 'More Than 2 Culture Female', preview: 'culture-female' },
      { id: 'immigration-male', label: 'Immigration - Male', preview: 'immigration-male' },
      { id: 'immigration-female', label: 'Immigration - Female', preview: 'immigration-female' },
    ]
  },
  gender: {
    name: 'Gender Based',
    shapes: [
      { id: 'classic-female', label: 'Female (Classic)', preview: 'classic-female', executeCategory: 'genogram' },
      { id: 'classic-male', label: 'Male (Classic)', preview: 'classic-male', executeCategory: 'genogram' },
      { id: 'agender', label: 'Agender / Genderless', preview: 'agender', executeCategory: 'genogram' },
      { id: 'epicene-classic', label: 'Epicene (Classic)', preview: 'epicene-classic', executeCategory: 'genogram' },
      { id: 'gender-fluid', label: 'Gender Fluid', preview: 'gender-fluid', executeCategory: 'genogram' },
      { id: 'transgender-classic', label: 'Transgender', preview: 'transgender-classic', executeCategory: 'genogram' },
      { id: 'transgender-alt', label: 'Transgender', preview: 'transgender-alt', executeCategory: 'genogram' },
      { id: 'genderqueer', label: 'Genderqueer', preview: 'genderqueer', executeCategory: 'genogram' },
      { id: 'agender-classic', label: 'Agender / Genderless (Classic)', preview: 'agender-classic', executeCategory: 'genogram' },
      { id: 'epicene', label: 'Epicene', preview: 'epicene', executeCategory: 'genogram' },
      { id: 'androgyne', label: 'Androgyne', preview: 'androgyne', executeCategory: 'genogram' },
      { id: 'xenogender', label: 'Xenogender', preview: 'xenogender', executeCategory: 'genogram' },
      { id: 'bigender', label: 'Bigender', preview: 'bigender', executeCategory: 'genogram' },
      { id: 'trigender', label: 'Trigender', preview: 'trigender', executeCategory: 'genogram' },
      { id: 'pangender', label: 'Pangender', preview: 'pangender', executeCategory: 'genogram' },
      { id: 'gender-fluid-classic', label: 'Gender Fluid (Classic)', preview: 'gender-fluid-classic', executeCategory: 'genogram' },
      { id: 'demigender', label: 'Demigender', preview: 'demigender', executeCategory: 'genogram' },
      { id: 'demiboy', label: 'Demiboy', preview: 'demiboy', executeCategory: 'genogram' },
      { id: 'demigirl', label: 'Demigirl', preview: 'demigirl', executeCategory: 'genogram' },
      { id: 'intergender', label: 'Intergender', preview: 'intergender', executeCategory: 'genogram' },
      { id: 'travesti', label: 'Travesti', preview: 'travesti', executeCategory: 'genogram' },
      { id: 'third-gender', label: 'Third Gender', preview: 'third-gender', executeCategory: 'genogram' },
      { id: 'hijra', label: 'Hijra', preview: 'hijra', executeCategory: 'genogram' },
      { id: 'neurogender', label: 'Neurogender', preview: 'neurogender', executeCategory: 'genogram' },
      { id: 'detragender', label: 'Detragender', preview: 'detragender', executeCategory: 'genogram' },
      { id: 'centrgender', label: 'Centrgender', preview: 'centrgender', executeCategory: 'genogram' },
      { id: 'isogender', label: 'Isogender', preview: 'isogender', executeCategory: 'genogram' },
      { id: 'ultergender-equal', label: 'Ultergender (Equal)', preview: 'ultergender-equal', executeCategory: 'genogram' },
      { id: 'male-equal', label: 'Male (Equal)', preview: 'male-equal', executeCategory: 'genogram' },
      { id: 'female-equal', label: 'Female (Equal)', preview: 'female-equal', executeCategory: 'genogram' },
      { id: 'trigender-equal', label: 'Trigender (Equal)', preview: 'trigender-equal', executeCategory: 'genogram' },
      { id: 'intersex', label: 'Intersex', preview: 'intersex', executeCategory: 'genogram' },
      { id: 'two-spirit', label: 'Two-Spirit', preview: 'two-spirit', executeCategory: 'genogram' },
      { id: 'ultergender', label: 'Ultergender', preview: 'ultergender', executeCategory: 'genogram' },
      { id: 'questioning', label: 'Questioning', preview: 'questioning', executeCategory: 'genogram' },
      { id: 'queer', label: 'Queer', preview: 'queer', executeCategory: 'genogram' },
      { id: 'polyamorous', label: 'Polyamorous', preview: 'polyamorous', executeCategory: 'genogram' },
      { id: 'self-partnering', label: 'Self-Partnering', preview: 'self-partnering', executeCategory: 'genogram' },
      { id: 'nonamorous', label: 'Nonamorous', preview: 'nonamorous', executeCategory: 'genogram' },
      { id: 'ambiamorous', label: 'Ambiamorous', preview: 'ambiamorous', executeCategory: 'genogram' },
      { id: 'polyflexible', label: 'Polyflexible', preview: 'polyflexible', executeCategory: 'genogram' },
      { id: 'monoflexible', label: 'Monoflexible', preview: 'monoflexible', executeCategory: 'genogram' },
    ],
  },
  basic: {
    name: 'Basic Shapes',
    shapes: [
      { id: 'rectangle', label: 'Rectangle', icon: Square },
      { id: 'circle', label: 'Circle', icon: Circle },
      { id: 'triangle', label: 'Triangle', icon: Triangle },
      { id: 'diamond', label: 'Diamond', icon: Diamond },
      { id: 'hexagon', label: 'Hexagon', icon: Hexagon },
      { id: 'pentagon', label: 'Pentagon', icon: Pentagon },
      { id: 'star', label: 'Star', icon: Star },
      { id: 'table', label: 'Table', icon: Table },
      { id: 'line', label: 'Line', icon: Minus },
    ]
  },
  flowchart: {
    name: 'Flowchart',
    shapes: [
      ...AI_FLOWCHART_SHAPE_IDS.map((id) => ({
        id,
        label: toTitleCaseFromId(id),
        icon: createAiFlowchartIcon(id),
        defaultText: (AI_FLOWCHART_SHAPES as Record<string, { text?: string }>)[id]?.text,
      })),
      { id: 'flowchart-swimlane-vertical', label: 'Swim Lane - Vertical', icon: SwimlaneVerticalIcon },
      { id: 'flowchart-swimlane-horizontal', label: 'Swim Lane - Horizontal', icon: SwimlaneHorizontalIcon },
    ],
  },
  bpmn: {
    name: 'BPMN',
    shapes: [
      ...BPMN_SHAPE_IDS.map((id) => ({
        id,
        label: toTitleCaseFromId(id.replace(/^bpmn-/, '')),
        icon: createAiFlowchartIcon(id),
        defaultText: (BPMN_SHAPES as Record<string, { text?: string }>)[id]?.text,
      })),
      { id: 'flowchart-swimlane-vertical', label: 'Swim Lane - Vertical', icon: SwimlaneVerticalIcon },
      { id: 'flowchart-swimlane-horizontal', label: 'Swim Lane - Horizontal', icon: SwimlaneHorizontalIcon },
    ],
  },
//   tables: {
//     name: 'Tables & Matrices',
//   },
  org: {
    name: 'Org Chart',
    shapes: ORG_SHAPE_CATALOG.map((shape) => ({
      id: shape.id,
      label: shape.label,
      preview: shape.id,
    })),
  },
  network: {
    name: 'Cloud & Network',
    shapes: NETWORK_PICKER_SHAPES,
  }
};

const diagramTypeCategoryMapping: Record<string, string[]> = {
  genogram: ['genogram', 'gender', 'basic', 'tables'],
  org: ['org', 'basic'],
  flowchart: ['basic', 'flowchart', 'bpmn'],
  mindmap: ['basic'],
  network: ['network', 'basic'],
  database: ['basic'],
  uml: ['basic'],
  generic: ['basic'],
};

export const ShapePickerToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('genogram');
  
  // Merge hardcoded `shapeCategories` (legacy 7) with the plugin-registry
  // categories (new verticals: software-design, planning, ...). Lookup
  // helper mirrors `shapeCategories[key]` but falls through to the registry.
  const lookupCategory = React.useCallback(
    (key: string): ShapeCategory | RegisteredShapeCategory | undefined => {
      return (shapeCategories as Record<string, ShapeCategory>)[key]
        ?? NETWORK_SHAPE_CATEGORIES[key]
        ?? listRegisteredShapeCategories().find((c) => c.id === key);
    },
    [],
  );

  // Tabs keep a fixed, natural order — the selected shape set only drives which
  // tab is active on first open (see effect below), never the tab order.
  const networkCategoryKeys = React.useMemo(
    () => getOrderedNetworkShapeCategoryKeys(null),
    [],
  );

  const visibleCategoryKeys = React.useMemo(() => {
    const diagramType = context.diagramType || 'genogram';
    const allowedHardcoded = diagramType === 'network'
      ? [...networkCategoryKeys, 'basic']
      : diagramTypeCategoryMapping[diagramType] || ['basic'];
    const allowedRegistered = getRegisteredCategoryIdsForDiagramType(diagramType as DiagramType);
    const allowed = [...allowedHardcoded, ...allowedRegistered];

    // If selection is BPMN shapes, show only BPMN shapes in "+ create" dropdown.
    // This avoids mixing flowchart and BPMN options when user is working in BPMN context.
    const selectionIds = Array.from(context.selection ?? []);
    if (diagramType === 'flowchart' && selectionIds.length > 0) {
      const nodes = useDiagramStore.getState().nodes.filter((n) => selectionIds.includes(n.id));
      const isBpmnSelection =
        nodes.length > 0 &&
        nodes.every((rawNode) => {
          const node = rawNode as ShapePickerNodeLike;
          const attrs = node.attrs ?? {};
          const raw = (attrs.variant ?? attrs.shapeType ?? node.name ?? node.type ?? '') as string;
          return String(raw).toLowerCase().replace(/\s+/g, '-').startsWith('bpmn-');
        });
      if (isBpmnSelection) return ['bpmn'];
    }

    return allowed.filter((key) => Boolean(lookupCategory(key)));
  }, [context.diagramType, context.selection, lookupCategory, networkCategoryKeys]);

  const initialTab = React.useMemo(() => {
    if (context.diagramType && visibleCategoryKeys.includes(context.diagramType)) {
      return context.diagramType;
    }
    return visibleCategoryKeys[0] || 'genogram';
  }, [context.diagramType, visibleCategoryKeys]);

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const hasSearchQuery = normalizeShapePickerSearch(searchQuery).length > 0;

  // When the picker is opened programmatically (e.g. auto-opened on a blank
  // canvas first-load), the surrounding chrome is still mounting/animating and
  // can momentarily move focus or fire an interaction-outside, which Radix
  // interprets as a request to close — making the picker flicker open then shut.
  // We record the open timestamp and ignore the very next close that lands
  // inside this short grace window so the auto-open reliably sticks. User-driven
  // closes (Escape, outside click) after the window behave normally.
  const programmaticOpenAtRef = React.useRef(0);
  const PROGRAMMATIC_OPEN_GRACE_MS = 700;

  const handleOpenChange = React.useCallback((next: boolean) => {
    if (!next && Date.now() - programmaticOpenAtRef.current < PROGRAMMATIC_OPEN_GRACE_MS) {
      return;
    }
    setOpen(next);
  }, []);

  useEffect(() => {
    const shapePickerWindow = window as ShapePickerWindow;
    shapePickerWindow.__openShapePicker = () => {
      programmaticOpenAtRef.current = Date.now();
      setOpen(true);
    };
    return () => {
      delete shapePickerWindow.__openShapePicker;
    };
  }, []);

  // The FIRST time the picker opens on a network diagram, snap the active tab
  // to the persisted shape-set library so a freshly-started Blueprint lands on
  // the chosen provider's tab. This runs once only — subsequent opens preserve
  // whatever tab the user last selected, and the tab order is never changed.
  const didSnapShapeSetTabRef = React.useRef(false);
  useEffect(() => {
    if (!open || didSnapShapeSetTabRef.current) return;
    if (context.diagramType !== 'network') return;
    didSnapShapeSetTabRef.current = true;
    const libraryId = readNetworkDefaultShapeLibrary(context.doc);
    if (!libraryId) return;
    const key = toNetworkShapeCategoryKey(libraryId);
    if (visibleCategoryKeys.includes(key)) {
      setActiveTab(key);
    }
  }, [open, context.diagramType, context.doc, visibleCategoryKeys]);

  useEffect(() => {
    if (!visibleCategoryKeys.includes(activeTab)) {
      setActiveTab(initialTab);
    }
  }, [activeTab, initialTab, visibleCategoryKeys]);

  useEffect(() => {
    if (!open && searchQuery) {
      setSearchQuery('');
    }
  }, [open, searchQuery]);
  
  // Helper function to translate shape labels
  const getShapeLabel = (category: string, shapeId: string, fallbackLabel: string): string => {
    if (category === 'org') {
      return i18n.t(`diagram:orgchart.shapeCatalog.${shapeId}`, { defaultValue: fallbackLabel }) as string;
    }
    const isBpmnPanelShape =
      category === 'bpmn' ||
      (category === 'flowchart' &&
        (shapeId === 'flowchart-swimlane-vertical' || shapeId === 'flowchart-swimlane-horizontal'));
    if (isBpmnPanelShape) {
      return i18n.t(`diagram:shapePicker.bpmnShapes.${shapeId}`, { defaultValue: fallbackLabel }) as string;
    }
    // Convert shape ID to camelCase for translation key
    const translationKey = shapeId
      .split('-')
      .map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
    return t(`shapes.${translationKey}`, { defaultValue: fallbackLabel }) as string;
  };

  const canCreateConnector = context.selection.size === 2;

  const filterShapesForCategory = (
    category: string,
    shapes: readonly ShapePickerShape[],
  ): ShapePickerShape[] =>
    filterShapePickerItems(shapes, searchQuery, (shape) => [
      shape.id,
      shape.label,
      getShapeLabel(category, shape.id, shape.label),
      shape.defaultText,
      shape.preview,
      shape.variant,
      shape.executeCategory,
      shape.libraryId,
    ]);

  const filterConnectorsForCategory = (
    connectors: NonNullable<ShapeCategory['connectors']>,
  ): NonNullable<ShapeCategory['connectors']> =>
    filterShapePickerItems(connectors, searchQuery, (connector) => [
      connector.id,
      connector.label,
      connector.variant,
    ]);

  const getCategoryDisplayName = (category: string): string => {
    const categoryData = lookupCategory(category);
    return t(`shapes.categories.${category}`, { defaultValue: categoryData?.name ?? toTitleCaseFromId(category) });
  };

  const getShapeSearchContext = (category: string, shape: ShapePickerShape): string => {
    if (shape.libraryId) {
      return getCategoryDisplayName(toNetworkShapeCategoryKey(shape.libraryId));
    }
    return getCategoryDisplayName(category);
  };

  const handleShapeSelect = (category: string, shapeId: string, shape?: { executeCategory?: string }) => {
    const categoryData = lookupCategory(category);
    const isConnector = categoryData?.connectors?.some(c => c.id === shapeId);
    const targetCategory = isConnector ? category : (shape?.executeCategory ?? category);
    onExecute({ category: targetCategory, shapeId });
    if (!isConnector) setOpen(false);
  };

  const handleDragStart = (e: React.DragEvent, category: string, shapeId: string, shape: ShapePickerShape) => {
    // Check write permissions before allowing drag start
    if (!context.canWrite) {
      e.preventDefault();
      return;
    }
    
    const targetCategory = shape?.executeCategory ?? category;
    e.dataTransfer.effectAllowed = 'copy';
    const payload = (() => {
      // Special-case: Swimlane vertical (from flowchart or bpmn panel) → table schema.
      if (shapeId === 'flowchart-swimlane-vertical') {
        return {
          category: 'tables',
          shapeId: 'table',
          schemaId: 'swimlane-vertical',
          label: shape.label,
          preview: shape.preview,
          icon: getShapeIconName(shape),
        };
      }
      // Special-case: Swimlane horizontal (from flowchart or bpmn panel) → table schema.
      if (shapeId === 'flowchart-swimlane-horizontal') {
        return {
          category: 'tables',
          shapeId: 'table',
          schemaId: 'swimlane-horizontal',
          label: shape.label,
          preview: shape.preview,
          icon: getShapeIconName(shape),
        };
      }
      return {
        category: targetCategory,
        shapeId,
        label:
          targetCategory === 'org'
            ? (i18n.t(`diagram:orgchart.shapeCatalog.${shapeId}`, { defaultValue: shape.label }) as string)
            : shape.label,
        preview: shape.preview,
        icon: getShapeIconName(shape),
        text:
          targetCategory === 'flowchart'
            ? getTranslatedFlowchartShapeLabel(shapeId)
            : shape.defaultText,
      };
    })();
    e.dataTransfer.setData('application/x-shape-picker', JSON.stringify(payload));

    // Create a custom drag image
    const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
    dragImage.style.opacity = '0.8';
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 20, 20);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const renderShapeGrid = (
    shapes: ShapePickerShape[],
    category: string,
    options: { testId?: string } = {},
  ) => {
    const showLabels = true;
    const useWideLayout = category === 'genogram' || category === 'gender' || category === 'basic' || category === 'org';
    const isFlowchart = category === 'flowchart' || category === 'bpmn';
    const iconSize = useWideLayout ? 'h-7 w-7' : (isFlowchart ? 'h-6 w-6' : 'h-5 w-5');
    
    const renderShapePreview = (shape: ShapePickerShape) => {
      if (!shape.preview) {
        if (shape.iconUrl) {
          return (
            <img
              alt=""
              src={shape.iconUrl}
              loading="lazy"
              decoding="async"
              className={cn(iconSize, 'object-contain transition-opacity duration-150 group-hover:opacity-90')}
              draggable={false}
            />
          );
        }
        const Icon = shape.icon ?? Cloud;
        return <Icon className={cn(iconSize, "text-slate-600 dark:text-slate-200 group-hover:text-slate-900 group-hover:dark:text-slate-50 transition-colors duration-150")} />;
      }
      
      if (category === 'org') {
        return (
          <React.Suspense fallback={null}>
            <OrgShapePreview
              shapeType={shape.preview as OrgPreviewShapeType}
              size={32}
              className="transition-colors duration-150"
            />
          </React.Suspense>
        );
      }
      
      return (
        <GenogramShapePreview
          shapeType={shape.preview as GenogramPreviewShapeType}
          size={32}
          className="transition-colors duration-150"
        />
      );
    };

    return (
      <div
        className={cn(
          "grid gap-3 p-3 justify-items-center",
          useWideLayout ? "grid-cols-4" : "grid-cols-5 gap-2 p-2"
        )}
        data-testid={options.testId ?? `tid.tools.shapePicker.grid.${category}`}
      >
        {shapes.map((shape) => {
          return (
            <button
              key={shape.id}
              draggable
              onClick={() => handleShapeSelect(category, shape.id, shape)}
              onDragStart={(e) => handleDragStart(e, category, shape.id, shape)}
              className={cn(
                "group flex flex-col items-center justify-center gap-1.5 rounded-lg border border-transparent p-2",
                "transition-colors duration-150 cursor-move",
                "hover:bg-muted/70 hover:border-muted-foreground/40 dark:hover:bg-slate-700/60 dark:hover:border-slate-500",
                useWideLayout ? "min-h-[60px] w-16" : (showLabels ? "min-h-[60px] w-16" : "w-12 h-12")
              )}
              aria-label={getShapeLabel(category, shape.id, shape.label)}
              data-testid={`tid.tools.shapePicker.item.${category}.${shape.id}`}
            >
              {renderShapePreview(shape)}
              {showLabels && (
                <span className="text-[10px] text-slate-600 dark:text-slate-300 group-hover:text-slate-900 group-hover:dark:text-slate-50 text-center leading-tight max-w-[80px] transition-colors duration-150">
                  {getShapeLabel(category, shape.id, shape.label)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const renderSearchShapeIcon = (shape: ShapePickerShape, category: string) => {
    if (shape.preview) {
      if (category === 'org') {
        return (
          <React.Suspense fallback={null}>
            <OrgShapePreview
              shapeType={shape.preview as OrgPreviewShapeType}
              size={30}
              className="transition-colors duration-150"
            />
          </React.Suspense>
        );
      }

      return (
        <GenogramShapePreview
          shapeType={shape.preview as GenogramPreviewShapeType}
          size={30}
          className="transition-colors duration-150"
        />
      );
    }

    if (shape.iconUrl) {
      return (
        <img
          alt=""
          src={shape.iconUrl}
          loading="lazy"
          decoding="async"
          className="h-6 w-6 object-contain transition-opacity duration-150 group-hover:opacity-90"
          draggable={false}
        />
      );
    }

    const Icon = shape.icon ?? Cloud;
    return (
      <Icon className="h-5 w-5 text-slate-600 transition-colors duration-150 group-hover:text-slate-900 dark:text-slate-200 group-hover:dark:text-slate-50" />
    );
  };

  const renderSearchShapeList = (shapes: ShapePickerShape[], category: string) => (
    <div
      className="flex flex-col gap-1 p-2"
      data-testid={`tid.tools.shapePicker.searchList.${category}`}
    >
      {shapes.map((shape) => {
        const label = getShapeLabel(category, shape.id, shape.label);
        return (
          <button
            key={shape.id}
            draggable
            onClick={() => handleShapeSelect(category, shape.id, shape)}
            onDragStart={(e) => handleDragStart(e, category, shape.id, shape)}
            className={cn(
              'group flex w-full items-center gap-3 rounded-md border border-transparent px-2.5 py-2 text-left',
              'cursor-move transition-colors duration-150',
              'hover:bg-muted/70 hover:border-muted-foreground/40 dark:hover:bg-slate-700/60 dark:hover:border-slate-500'
            )}
            aria-label={label}
            data-testid={`tid.tools.shapePicker.item.${category}.${shape.id}`}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted/30">
              {renderSearchShapeIcon(shape, category)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-xs font-medium leading-snug text-slate-800 break-words dark:text-slate-100">
                {label}
              </span>
              <span className="truncate text-[10px] leading-tight text-slate-500 dark:text-slate-400">
                {getShapeSearchContext(category, shape)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderConnectorGrid = (connectors: NonNullable<ShapeCategory['connectors']>, category: string) => (
    <div>
      {!canCreateConnector && (
        <div className="px-2 py-1 text-xs text-amber-600 bg-amber-50 border-b border-amber-100">
          {i18n.t('diagram:shapePicker.selectTwoShapesToConnect', { defaultValue: 'Select 2 shapes to connect' }) as string}
        </div>
      )}
      <div className="grid grid-cols-5 gap-2 p-2" data-testid={`tid.tools.shapePicker.connectorGrid.${category}`}>
        {connectors.map((connector) => {
          const Icon = connector.icon;
          return (
            <button
              key={connector.id}
              onClick={() => handleShapeSelect(category, connector.id)}
              disabled={!canCreateConnector}
              className={cn(
                "group w-12 h-12 flex flex-col items-center justify-center gap-1 rounded-lg border border-transparent transition-colors duration-150",
                canCreateConnector
                  ? "hover:bg-muted/70 hover:border-muted-foreground/40 dark:hover:bg-slate-700/60 dark:hover:border-slate-500 cursor-pointer"
                  : "opacity-50 cursor-not-allowed"
              )}
              aria-label={
                canCreateConnector
                  ? connector.label
                  : (i18n.t('diagram:shapePicker.selectTwoShapesFirst', { defaultValue: 'Select 2 shapes first' }) as string)
              }
              data-testid={`tid.tools.shapePicker.connectorItem.${category}.${connector.id}`}
            >
              <Icon
                className={cn(
                  "h-5 w-5 transition-colors duration-150",
                  canCreateConnector
                    ? "text-slate-600 dark:text-slate-200 group-hover:text-slate-900 group-hover:dark:text-slate-50"
                    : "text-slate-400 dark:text-slate-500",
                  connector.variant === 'dashed' && "stroke-dasharray-2"
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderSearchConnectorList = (connectors: NonNullable<ShapeCategory['connectors']>, category: string) => (
    <div>
      {!canCreateConnector && (
        <div className="px-2 py-1 text-xs text-amber-600 bg-amber-50 border-b border-amber-100">
          {i18n.t('diagram:shapePicker.selectTwoShapesToConnect', { defaultValue: 'Select 2 shapes to connect' }) as string}
        </div>
      )}
      <div
        className="flex flex-col gap-1 p-2"
        data-testid={`tid.tools.shapePicker.connectorSearchList.${category}`}
      >
        {connectors.map((connector) => {
          const Icon = connector.icon;
          return (
            <button
              key={connector.id}
              onClick={() => handleShapeSelect(category, connector.id)}
              disabled={!canCreateConnector}
              className={cn(
                'group flex w-full items-center gap-3 rounded-md border border-transparent px-2.5 py-2 text-left',
                'transition-colors duration-150',
                canCreateConnector
                  ? 'cursor-pointer hover:bg-muted/70 hover:border-muted-foreground/40 dark:hover:bg-slate-700/60 dark:hover:border-slate-500'
                  : 'cursor-not-allowed opacity-50'
              )}
              aria-label={
                canCreateConnector
                  ? connector.label
                  : (i18n.t('diagram:shapePicker.selectTwoShapesFirst', { defaultValue: 'Select 2 shapes first' }) as string)
              }
              data-testid={`tid.tools.shapePicker.connectorItem.${category}.${connector.id}`}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted/30">
                <Icon
                  className={cn(
                    'h-5 w-5 transition-colors duration-150',
                    canCreateConnector
                      ? 'text-slate-600 group-hover:text-slate-900 dark:text-slate-200 group-hover:dark:text-slate-50'
                      : 'text-slate-400 dark:text-slate-500',
                    connector.variant === 'dashed' && 'stroke-dasharray-2'
                  )}
                />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-xs font-medium leading-snug text-slate-800 break-words dark:text-slate-100">
                  {connector.label}
                </span>
                <span className="truncate text-[10px] leading-tight text-slate-500 dark:text-slate-400">
                  {getCategoryDisplayName(category)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderNoSearchResults = (category: string) => (
    <div
      className="flex h-full items-center justify-center px-6 text-center text-xs text-slate-500 dark:text-slate-400"
      data-testid={`tid.tools.shapePicker.empty.${category}`}
    >
      {i18n.t('diagram:shapePicker.noSearchResults', { defaultValue: 'No matching shapes' }) as string}
    </div>
  );

  const renderOrgShapeList = (shapes: ShapePickerShape[]) => {
    const renderOrgShapePreview = (shape: ShapePickerShape) => (
      <React.Suspense fallback={null}>
        <OrgShapePreview
          shapeType={shape.preview as OrgPreviewShapeType}
          size={28}
          className="transition-colors duration-150"
        />
      </React.Suspense>
    );

    return (
      <div
        className="flex flex-col gap-1 p-2"
        data-testid="tid.tools.shapePicker.list.org"
      >
        {shapes.map((shape) => (
          <button
            key={shape.id}
            draggable
            onClick={() => handleShapeSelect('org', shape.id, shape)}
            onDragStart={(e) => handleDragStart(e, 'org', shape.id, shape)}
            className={cn(
              'group flex w-full items-center gap-3 rounded-lg border border-transparent px-2 py-2',
              'transition-colors duration-150 cursor-move',
              'hover:bg-muted/70 hover:border-muted-foreground/40 dark:hover:bg-slate-700/60 dark:hover:border-slate-500'
            )}
            aria-label={getShapeLabel('org', shape.id, shape.label)}
            data-testid={`tid.tools.shapePicker.item.org.${shape.id}`}
          >
            <span className="flex h-8 w-10 items-center justify-center shrink-0">
              {renderOrgShapePreview(shape)}
            </span>
            <span className="text-xs text-slate-700 dark:text-slate-200 group-hover:text-slate-900 group-hover:dark:text-slate-50 text-left leading-tight">
              {getShapeLabel('org', shape.id, shape.label)}
            </span>
          </button>
        ))}
      </div>
    );
  };

  const getCurrentIcon = () => {
    const category = lookupCategory(activeTab);
    const shapes = category?.shapes;
    if (shapes && shapes.length > 0) {
      const first = shapes[0] as ShapePickerShape;
      if (first?.icon) {
        const Icon = first.icon;
        return <Icon className="h-5 w-5 text-slate-600 dark:text-slate-200" />;
      }
      if (first?.preview) {
        return (
          <GenogramShapePreview
            shapeType={first.preview as GenogramPreviewShapeType}
            size={20}
            className="transition-colors duration-150"
          />
        );
      }
    }
    return <Square className="h-5 w-5 text-slate-600 dark:text-slate-200" />;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="w-0 h-0 opacity-0 pointer-events-none fixed bottom-6 left-[calc(50%-200px)]"
          data-testid="tid.tools.shapePicker.trigger"
        >
          {getCurrentIcon()}
          <ChevronDown className="h-3 w-3 absolute bottom-1 right-1 text-[#94A3B8] opacity-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="min-w-[440px] w-[440px] p-0 h-[454px]"
        align="center"
        side="top"
        sideOffset={80}
        data-testid="tid.tools.shapePicker.panel"
      >
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full flex-col">
          <TabsList className="w-full justify-start rounded-t-md rounded-b-none border-none h-10 bg-background overflow-hidden">
            {visibleCategoryKeys.map((key) => {
              const category = lookupCategory(key);
              if (!category) return null;
              return (
              <TabsTrigger
                key={key}
                value={key}
                className="shrink-0 px-2 text-xs data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#3478DD] rounded-none"
                data-testid={`tid.tools.shapePicker.tab.${key}`}
              >
                {t(`shapes.categories.${key}`, { defaultValue: category.name })}
              </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="border-y border-border bg-background px-2 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={i18n.t('diagram:shapePicker.searchPlaceholder', { defaultValue: 'Search shapes' }) as string}
                className="h-8 pl-8 pr-8 text-xs"
                data-testid="tid.tools.shapePicker.search"
              />
              {hasSearchQuery && (
                <button
                  type="button"
                  aria-label={i18n.t('diagram:shapePicker.clearSearch', { defaultValue: 'Clear search' }) as string}
                  className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded hover:bg-muted"
                  onClick={() => setSearchQuery('')}
                  data-testid="tid.tools.shapePicker.search.clear"
                >
                  <X className="h-3.5 w-3.5 text-slate-500" />
                </button>
              )}
            </div>
          </div>

          {visibleCategoryKeys.map((key) => {
            const category = lookupCategory(key);
            if (!category) return null;
            const filteredShapes = category.shapes
              ? filterShapesForCategory(key, category.shapes as ShapePickerShape[])
              : undefined;
            const filteredConnectors = category.connectors
              ? filterConnectorsForCategory(category.connectors)
              : undefined;
            const hasFilteredResults =
              (filteredShapes?.length ?? 0) > 0 ||
              (filteredConnectors?.length ?? 0) > 0;
            return (
            <TabsContent
              key={key}
              value={key}
              className="m-0 flex min-h-0 flex-1 flex-col"
              data-testid={`tid.tools.shapePicker.tabPanel.${key}`}
            >
              <div className="flex-1 overflow-y-auto bg-background">
                {key === 'tables' ? (
                  <TableTemplatePicker
                    searchQuery={searchQuery}
                    onTemplateCreated={() => setOpen(false)}
                  />
                ) : (
                  <>
                    {filteredShapes && filteredShapes.length > 0 && (
                      <div>
                        {key !== 'genogram' && key !== 'gender' && (
                          <div className="text-xs font-regular text-slate-600 dark:text-slate-300 px-2 pt-2">
                            {i18n.t('diagram:tools.shapes', { defaultValue: 'Shapes' }) as string}
                          </div>
                        )}
                        {key === 'org'
                          ? (hasSearchQuery ? renderSearchShapeList(filteredShapes, key) : renderOrgShapeList(filteredShapes))
                          : hasSearchQuery
                            ? renderSearchShapeList(filteredShapes, key)
                          : renderShapeGrid(filteredShapes, key)}
                      </div>
                    )}
                    {filteredConnectors && filteredConnectors.length > 0 && key !== 'genogram' && (
                      <div>
                        <div className="text-xs font-regular text-slate-600 dark:text-slate-300 px-2 pt-2">
                          {i18n.t('diagram:tools.connectors', { defaultValue: 'Connectors' }) as string}
                        </div>
                        {hasSearchQuery
                          ? renderSearchConnectorList(filteredConnectors, key)
                          : renderConnectorGrid(filteredConnectors, key)}
                      </div>
                    )}
                    {hasSearchQuery && !hasFilteredResults && renderNoSearchResults(key)}
                  </>
                )}
              </div>
            </TabsContent>
            );
          })}
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};

export class ShapePickerTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'shape-picker',
    name: i18n.t('diagram:tools.shapes', { defaultValue: 'Shapes' }) as string,
    icon: 'shapes',
    category: 'create',
    description: i18n.t('diagram:shapePicker.toolDescription', { defaultValue: 'Add shapes and connectors' }) as string,
    version: '1.0.0',
    
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: false,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    }
  };

  private placing = false;
  private chosen: { category: string; shapeId: string } | null = null;
  private startPoint: { x: number; y: number } | null = null;
  private isShiftPressed: boolean = false;
  private constraintDirection: 'horizontal' | 'vertical' | null = null;
  private readonly CONSTRAINT_THRESHOLD = 5; // pixels to avoid jitter

  private getOrgViewMode(): 'compact' | 'full' {
    try {
      const settingsMap = this.context?.doc?.getMap?.('diagramSettings');
      const settingsJson = settingsMap?.get?.('settings');
      return resolveShapePickerOrgViewMode(settingsJson);
    } catch {
      return resolveShapePickerOrgViewMode(undefined);
    }
  }

  private getDefaultDimensions(): { width: number; height: number } {
    if (!this.chosen) {
      return { width: 80, height: 80 };
    }

    // Normalize category - gender maps to genogram
    const category = this.chosen.category === 'gender' ? 'genogram' : this.chosen.category;
    const registeredShape = resolveRegisteredShapeCategoryManifest(category, this.chosen.shapeId);
    const registeredDimensions = resolveShapePickerManifestDimensions({
      category,
      shapeId: this.chosen.shapeId,
      manifestDimensions: registeredShape?.manifest.defaultDimensions,
      orgViewMode: this.getOrgViewMode(),
    });
    if (registeredDimensions) {
      return registeredDimensions;
    }

    const diagramType: DiagramType = (() => {
      switch (category) {
        case 'genogram':
        case 'text':
        case 'sticky-note':
          return 'genogram';
        case 'basic':
        case 'flowchart':
        case 'bpmn':
          return 'flowchart';
        case 'orgchart':
        case 'org':
          return 'org';
        case 'network':
          return 'network';
        default:
          return 'genogram';
      }
    })();

    let manifestShapeId = this.chosen.shapeId;
    // All genogram/gender category shapes map to person manifest for dimensions
    // (they're all person variants, just with different properties)
    if (category === 'genogram' || this.chosen.category === 'gender') {
      manifestShapeId = 'person';
    } else if (category === 'text' || this.chosen.shapeId === 'text') {
      manifestShapeId = 'text';
    } else if (category === 'sticky-note' || this.chosen.shapeId === 'sticky-note') {
      manifestShapeId = 'sticky-note';
    }

    if (
      (category === 'flowchart' || category === 'bpmn') &&
      (this.chosen.shapeId === 'flowchart-swimlane-vertical' || this.chosen.shapeId === 'flowchart-swimlane-horizontal')
    ) {
      const schemaId = this.chosen.shapeId === 'flowchart-swimlane-horizontal' ? 'swimlane-horizontal' : 'swimlane-vertical';
      const schema = getTableSchema(schemaId);
      if (schema) {
        const rowCount = typeof schema.grid.rows === 'number' && Number.isFinite(schema.grid.rows) ? Math.max(1, Math.floor(schema.grid.rows)) : 1;
        const colCount = typeof schema.grid.cols === 'number' && Number.isFinite(schema.grid.cols) ? Math.max(1, Math.floor(schema.grid.cols)) : 1;
        const defaultRowHeight = schema.dimensions?.defaultRowHeight;
        const defaultColWidth = schema.dimensions?.defaultColWidth;
        const headerRow0 = schema.rows?.find((r) => r.index === 0);
        const showHeader = !!headerRow0?.isHeader;
        const headerHeight = typeof headerRow0?.height === 'number' && Number.isFinite(headerRow0.height) ? headerRow0.height : 32;

        const width = typeof defaultColWidth === 'number' && Number.isFinite(defaultColWidth) ? defaultColWidth * colCount : 240;
        const height =
          typeof defaultRowHeight === 'number' && Number.isFinite(defaultRowHeight)
            ? (defaultRowHeight * rowCount) + (showHeader ? headerHeight : 0)
            : 180;
        return { width, height };
      }
    }

    const manifest = getShapeManifest(diagramType, manifestShapeId);
    if (manifest?.defaultDimensions) {
      return resolveShapePickerManifestDimensions({
        category,
        shapeId: manifestShapeId,
        manifestDimensions: manifest.defaultDimensions,
        orgViewMode: this.getOrgViewMode(),
      }) ?? manifest.defaultDimensions;
    }

    if (category === 'bpmn') {
      const def = (BPMN_SHAPES as Record<string, { width: number; height: number } | undefined>)[manifestShapeId];
      if (def && Number.isFinite(def.width) && Number.isFinite(def.height)) {
        return { width: def.width, height: def.height };
      }
    }

    if (manifestShapeId === 'text') return { width: 100, height: 30 };
    if (manifestShapeId === 'sticky-note') return { width: 150, height: 150 };
    if (category === 'basic') return { width: 120, height: 80 };
    if (category === 'flowchart') return { width: 140, height: 80 };
    if (category === 'bpmn') return { width: 120, height: 80 };
    if (category === 'orgchart' || category === 'org') {
      // Fallback dimensions if manifest lookup failed - align with org view mode.
      const orgDimensions = resolveShapePickerManifestDimensions({
        category,
        shapeId: manifestShapeId,
        orgViewMode: this.getOrgViewMode(),
      });
      if (orgDimensions) return orgDimensions;
      return { width: 270, height: 80 }; // Default to legacy org-card dimensions
    }
    return { width: 80, height: 80 };
  }

  private setStageCursor(cursor: string): void {
    const container = this.context?.stage?.container();
    if (container) {
      container.style.cursor = cursor;
    }
  }

  onActivate(context: IToolContext): void {
    super.onActivate(context);
    if (this.placing) {
      this.setStageCursor('crosshair');
    }
    if (this.context?.stage) {
      const stage = this.context.stage;
      stage.on('mousedown.place-shape', this.handleMouseDown);
      stage.on('mousemove.place-shape', this.handleMouseMove);
      stage.on('mouseup.place-shape', this.handleMouseUp);
    }
    // Add keyboard handlers for shift key constraint
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    window.addEventListener('keydown', this.handleKeyDown, { capture: true });
    window.addEventListener('keyup', this.handleKeyUp, { capture: true });
  }

  onDeactivate(): void {
    if (this.context?.stage) {
      this.context.stage.off('.place-shape');
      this.setStageCursor('default');
    }
    // Remove keyboard handlers
    window.removeEventListener('keydown', this.handleKeyDown, { capture: true });
    window.removeEventListener('keyup', this.handleKeyUp, { capture: true });
    // Reset shift state
    this.isShiftPressed = false;
    this.constraintDirection = null;
    super.onDeactivate();
  }

  private handleMouseDown = () => {
    if (!this.placing || !this.context?.stage) return;
    const stage = this.context.stage;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    const scale = stage.scaleX();
    const origin = stage.position();
    const cx = (pos.x - origin.x) / scale;
    const cy = (pos.y - origin.y) / scale;
    this.startPoint = { x: cx, y: cy };
    // Reset constraint direction when starting a new drag
    this.constraintDirection = null;

    // Handle line preview start
    if (this.chosen?.shapeId === 'line') {
      this.context.eventBus.emit('place:preview:start', {
        fromPoint: { x: cx, y: cy },
        toPoint: { x: cx, y: cy },
        category: this.chosen.category,
        shapeId: this.chosen.shapeId
      });
    } else {
      this.context.eventBus.emit('place:preview:start', {
        x: cx,
        y: cy,
        width: 1,
        height: 1,
        category: this.chosen?.category,
        shapeId: this.chosen?.shapeId
      });
    }
  };

  private handleMouseMove = () => {
    if (!this.placing || !this.context?.stage) return;
    const stage = this.context.stage;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    const scale = stage.scaleX();
    const origin = stage.position();
    const cx = (pos.x - origin.x) / scale;
    const cy = (pos.y - origin.y) / scale;

    // Handle line preview differently
    if (this.chosen?.shapeId === 'line') {
      if (this.startPoint) {
        // Apply shift constraint if shift is pressed
        let toPointX = cx;
        let toPointY = cy;
        
        if (this.isShiftPressed) {
          // Determine constraint direction if not already set
          if (!this.constraintDirection) {
            this.determineConstraintDirection(cx, cy);
          }
          
          // Apply constraint based on direction
          if (this.constraintDirection === 'horizontal') {
            toPointY = this.startPoint.y; // Lock Y to start point
          } else if (this.constraintDirection === 'vertical') {
            toPointX = this.startPoint.x; // Lock X to start point
          }
        }
        
        // Show line from start point to current position (possibly constrained)
        this.context.eventBus.emit('place:preview:update', {
          fromPoint: this.startPoint,
          toPoint: { x: toPointX, y: toPointY },
          category: this.chosen.category,
          shapeId: this.chosen.shapeId
        });
      } else {
        // Show default horizontal line preview
        this.context.eventBus.emit('place:preview:update', {
          fromPoint: { x: cx - 50, y: cy },
          toPoint: { x: cx + 50, y: cy },
          category: this.chosen.category,
          shapeId: this.chosen.shapeId
        });
      }
      return;
    }

    // Regular shape preview
    if (this.startPoint) {
      const x = Math.min(this.startPoint.x, cx);
      const y = Math.min(this.startPoint.y, cy);
      const width = Math.abs(cx - this.startPoint.x);
      const height = Math.abs(cy - this.startPoint.y);
      this.context.eventBus.emit('place:preview:update', { 
        x, 
        y, 
        width, 
        height,
        category: this.chosen?.category,
        shapeId: this.chosen?.shapeId
      });
    } else {
      // Attach ghost to mouse with default size, top-left at pointer
      const defaultDims = this.getDefaultDimensions();
      this.context.eventBus.emit('place:preview:update', {
        x: cx,
        y: cy,
        width: defaultDims.width,
        height: defaultDims.height,
        category: this.chosen?.category,
        shapeId: this.chosen?.shapeId
      });
    }
  };

  private handleMouseUp = () => {
    if (!this.placing || !this.context?.stage || !this.chosen) return;
    
    // Check write permissions before allowing shape creation
    if (!this.context.canWrite) {
      this.context.eventBus.emit('place:preview:end');
      this.placing = false;
      this.chosen = null;
      this.startPoint = null;
      this.setStageCursor('default');
      return;
    }
    
    const stage = this.context.stage;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    const scale = stage.scaleX();
    const origin = stage.position();
    const cx = (pos.x - origin.x) / scale;
    const cy = (pos.y - origin.y) / scale;

    // Handle line creation differently
    if (this.chosen.shapeId === 'line') {
      const store = useDiagramStore.getState();

      // Check shape limits
      if (!validateShapeLimitOrPromptUpgrade(store)) {
        this.context.eventBus.emit('place:preview:end');
        this.placing = false;
        this.chosen = null;
        this.startPoint = null;
        this.setStageCursor('default');
        return;
      }

      let fromPoint: { x: number; y: number };
      let toPoint: { x: number; y: number };

      if (this.startPoint) {
        const distance = Math.sqrt(
          Math.pow(cx - this.startPoint.x, 2) + Math.pow(cy - this.startPoint.y, 2)
        );
        // If minimal drag, create default horizontal line
        if (distance < 3) {
          fromPoint = { x: cx - 50, y: cy };
          toPoint = { x: cx + 50, y: cy };
        } else {
          fromPoint = this.startPoint;
          
          // Apply shift constraint if shift is pressed
          let toPointX = cx;
          let toPointY = cy;
          
          if (this.isShiftPressed) {
            // Determine constraint direction if not already set
            if (!this.constraintDirection) {
              this.determineConstraintDirection(cx, cy);
            }
            
            // Apply constraint based on direction
            if (this.constraintDirection === 'horizontal') {
              toPointY = this.startPoint.y; // Lock Y to start point
            } else if (this.constraintDirection === 'vertical') {
              toPointX = this.startPoint.x; // Lock X to start point
            }
          }
          
          toPoint = { x: toPointX, y: toPointY };
        }
      } else {
        // Single click - create default horizontal line
        fromPoint = { x: cx - 50, y: cy };
        toPoint = { x: cx + 50, y: cy };
      }
      
      // Reset constraint direction after line creation
      this.constraintDirection = null;

      // Create edge with free endpoints
      const id = `edge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const freeFromId = `__freeF:${id}`;
      const freeToId = `__freeT:${id}`;

      const edgeAttrs = {
        fromPoint,
        toPoint,
        routingType: 'straight',
      };

      const cmd = new CreateEdgeCommand({
        id,
        type: 'line',
        from: freeFromId,
        to: freeToId,
        category: 'basic',
        attrs: edgeAttrs,
        version: 1,
        ydoc: this.context?.doc,
      });
      this.context.eventBus.emit('command:execute', cmd);

      // Select the newly created line
      const selectionStore = useDiagramStore.getState();
      selectionStore.setSelected(new Set([id]));

      this.context.eventBus.emit('place:preview:end');
      this.placing = false;
      this.chosen = null;
      this.startPoint = null;
      this.setStageCursor('default');
      return;
    }

    // Regular shape creation
    let x: number;
    let y: number;
    let width: number | undefined;
    let height: number | undefined;
    if (this.startPoint) {
      x = Math.min(this.startPoint.x, cx);
      y = Math.min(this.startPoint.y, cy);
      const w = Math.abs(cx - this.startPoint.x);
      const h = Math.abs(cy - this.startPoint.y);
      // If minimal drag, fall back to default size centered around release
      if (w < 3 && h < 3) {
        x = cx;
        y = cy;
        const defaultDims = this.getDefaultDimensions();
        width = defaultDims.width;
        height = defaultDims.height;
      } else {
        width = w;
        height = h;
      }
    } else {
      x = cx;
      y = cy;
      const defaultDims = this.getDefaultDimensions();
      width = defaultDims.width;
      height = defaultDims.height;
    }

    // Normalize category - gender maps to genogram for proper dimension handling
    const normalizedCategory = this.chosen.category === 'gender' ? 'genogram' : this.chosen.category;
    
    const shapeId = this.chosen.shapeId;
    if (normalizedCategory === 'flowchart') {
      if (shapeId === 'flowchart-swimlane-vertical') {
        const cmd = createTableFromSchema('swimlane-vertical', { x, y });
        this.context.eventBus.emit('command:execute', cmd);
      } else if (shapeId === 'flowchart-swimlane-horizontal') {
        const cmd = createTableFromSchema('swimlane-horizontal', { x, y });
        this.context.eventBus.emit('command:execute', cmd);
      } else {
        const label = getTranslatedFlowchartShapeLabel(shapeId);
        const cmd = new CreateShapeCommand({
          type: shapeId,
          x,
          y,
          width,
          height,
          text: label,
          category: normalizedCategory,
          ydoc: this.context?.doc,
        });
        this.context.eventBus.emit('command:execute', cmd);
      }
    } else if (normalizedCategory === 'bpmn') {
      if (shapeId === 'flowchart-swimlane-vertical') {
        const cmd = createTableFromSchema('swimlane-vertical', { x, y });
        this.context.eventBus.emit('command:execute', cmd);
      } else if (shapeId === 'flowchart-swimlane-horizontal') {
        const cmd = createTableFromSchema('swimlane-horizontal', { x, y });
        this.context.eventBus.emit('command:execute', cmd);
      } else {
        const label = getTranslatedBpmnShapeLabel(shapeId);
        const cmd = new CreateShapeCommand({
          type: shapeId,
          x,
          y,
          width,
          height,
          text: label,
          category: normalizedCategory,
          ydoc: this.context?.doc,
        });
        this.context.eventBus.emit('command:execute', cmd);
      }
    } else if (normalizedCategory === 'network') {
      const cmd = createNetworkElementCommand({
        type: shapeId,
        x,
        y,
        width,
        height,
      });
      this.context.eventBus.emit('command:execute', cmd);
    } else {
      const cmd = new CreateShapeCommand({
        type: shapeId,
        x,
        y,
        width,
        height,
        category: normalizedCategory,
        ydoc: this.context?.doc,
      });
      this.context.eventBus.emit('command:execute', cmd);
    }
    this.context.eventBus.emit('place:preview:end');
    this.placing = false;
    this.chosen = null;
    this.startPoint = null;
    this.setStageCursor('default');
  };

  execute(params?: { category: string; shapeId: string }): ICommand | null {
    if (!params) return null;
    
    // Check if this is a connector or a shape
    const categoryData = shapeCategories[params.category as keyof typeof shapeCategories];
    const isConnector = categoryData?.connectors?.some(c => c.id === params.shapeId);
    
    if (isConnector) {
      // Create edge/connector
      return new CreateEdgeCommand({
        type: params.shapeId,
        category: params.category,
        ydoc: this.context?.doc // Pass Yjs document for persistence
      });
    } else {
      // Enter placement mode
      // Normalize category - gender maps to genogram for consistency
      const normalizedCategory = params.category === 'gender' ? 'genogram' : params.category;
      this.chosen = { category: normalizedCategory, shapeId: params.shapeId };
      this.placing = true;
      this.setStageCursor('crosshair');
      // Seed preview at pointer if available (top-left at cursor)
      if (this.context?.stage?.getPointerPosition()) {
        const pos = this.context.stage.getPointerPosition()!;
        const scale = this.context.stage.scaleX();
        const origin = this.context.stage.position();
        const cx = (pos.x - origin.x) / scale;
        const cy = (pos.y - origin.y) / scale;
        const defaultDims = this.getDefaultDimensions();
        this.context.eventBus.emit('place:preview:start', {
          x: cx,
          y: cy,
          width: defaultDims.width,
          height: defaultDims.height,
          category: params.category,
          shapeId: params.shapeId
        });
      }
      return null;
    }
  }

  getToolbarComponent() {
    return ShapePickerToolComponent;
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    // Only handle shift key
    if (e.key === 'Shift' || e.keyCode === 16) {
      this.isShiftPressed = true;
      // If shift is pressed mid-drawing, we'll determine constraint direction on next mouse move
      // Don't reset constraintDirection here - let it persist if already set
    }
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    // Only handle shift key
    if (e.key === 'Shift' || e.keyCode === 16) {
      this.isShiftPressed = false;
      // Clear constraint direction when shift is released
      if (!this.placing) {
        this.constraintDirection = null;
      }
    }
  };

  private determineConstraintDirection(currentX: number, currentY: number): void {
    if (!this.startPoint) return;
    
    // Calculate movement from start point
    const deltaX = Math.abs(currentX - this.startPoint.x);
    const deltaY = Math.abs(currentY - this.startPoint.y);
    
    // Only determine direction if movement exceeds threshold (avoid jitter)
    const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    if (totalMovement < this.CONSTRAINT_THRESHOLD) {
      return; // Wait for more movement
    }
    
    // Determine constraint direction based on dominant movement
    if (deltaX > deltaY) {
      this.constraintDirection = 'horizontal';
    } else {
      this.constraintDirection = 'vertical';
    }
  }
}
